/**
 * Location utilities for Search Engine
 * Handles geocoding and location-based search functionality
 */

import { getCoordinatesFromPlaceId } from '../../Admin/clinics/utils/geocodingUtils'
import { calculateDistance } from './helpers'

/**
 * Convert place ID to coordinates for search
 * @param {string} placeId - Google place ID from GoogleAddress component
 * @returns {Promise<Object>} Coordinates with lat/lng for search
 */
export const getSearchCoordinatesFromPlaceId = async (placeId) => {
  try {
    const geoData = await getCoordinatesFromPlaceId(placeId)

    if (geoData && geoData.coordinates && geoData.coordinates.length === 2) {
      // Convert from MongoDB GeoJSON format [lng, lat] to search format {lat, lng}
      const [lng, lat] = geoData.coordinates
      return {
        lat,
        lng,
        success: true
      }
    }

    throw new Error('Invalid coordinates received')
  } catch (error) {
    console.error('Failed to get coordinates from place ID:', error)
    return {
      lat: null,
      lng: null,
      success: false,
      error: error.message
    }
  }
}

/**
 * Calculate distance between user location and clinic
 * @param {Object} userLocation - User's coordinates {lat, lng}
 * @param {Object} clinicGeolocation - Clinic's GeoJSON geolocation data
 * @returns {number|null} Distance in kilometers
 */
export const calculateClinicDistance = (userLocation, clinicGeolocation) => {
  if (!userLocation || !clinicGeolocation || !clinicGeolocation.coordinates) {
    return null
  }

  const { lat: userLat, lng: userLng } = userLocation
  const [clinicLng, clinicLat] = clinicGeolocation.coordinates // MongoDB format [lng, lat]

  if (!userLat || !userLng || !clinicLat || !clinicLng) {
    return null
  }

  return calculateDistance(userLat, userLng, clinicLat, clinicLng)
}

/**
 * Build MongoDB geospatial query for location-based search
 * @param {Object} userLocation - User's coordinates {lat, lng}
 * @param {number} maxDistance - Maximum distance in kilometers (default: 50km)
 * @returns {Object} MongoDB query object for geospatial search
 */
export const buildLocationQuery = (userLocation, maxDistance = 50) => {
  if (!userLocation || !userLocation.lat || !userLocation.lng) {
    return {}
  }

  // Convert km to meters for MongoDB
  const maxDistanceMeters = maxDistance * 1000

  return {
    geolocation: {
      $near: {
        $geometry: {
          type: 'Point',
          coordinates: [userLocation.lng, userLocation.lat] // [lng, lat] for MongoDB
        },
        $maxDistance: maxDistanceMeters
      }
    }
  }
}

/**
 * Sort clinics by distance
 * @param {Array} clinics - Array of clinic objects with distance property
 * @returns {Array} Sorted clinics array (closest first)
 */
export const sortClinicsByDistance = (clinics) => {
  return [...clinics].sort((a, b) => {
    // Clinics without distance go to the end
    if (a.distance === null && b.distance === null) return 0
    if (a.distance === null) return 1
    if (b.distance === null) return -1

    return a.distance - b.distance
  })
}

/**
 * Filter clinics by maximum distance
 * @param {Array} clinics - Array of clinic objects with distance property
 * @param {number} maxDistance - Maximum distance in kilometers
 * @returns {Array} Filtered clinics array
 */
export const filterClinicsByDistance = (clinics, maxDistance) => {
  if (!maxDistance) return clinics

  return clinics.filter((clinic) => {
    return clinic.distance === null || clinic.distance <= maxDistance
  })
}

/**
 * Add distance information to clinic records
 * @param {Array} clinics - Array of clinic objects
 * @param {Object} userLocation - User's coordinates {lat, lng}
 * @returns {Array} Clinics with distance information added
 */
export const addDistanceToClinic = (clinics, userLocation) => {
  if (!userLocation || !Array.isArray(clinics)) {
    return clinics
  }

  return clinics.map((clinic) => {
    const distance = calculateClinicDistance(userLocation, clinic.geolocation)
    return {
      ...clinic,
      distance,
      distanceFormatted: distance ? `${distance.toFixed(1)}km away` : 'Distance unknown'
    }
  })
}

/**
 * Process location-based search
 * Complete function to handle place ID to coordinates conversion and clinic filtering
 * @param {string} placeId - Google place ID from GoogleAddress
 * @param {Array} clinics - Array of clinic objects
 * @param {number} maxDistance - Maximum distance in kilometers
 * @returns {Promise<Object>} Processed search results with location data
 */
export const processLocationBasedSearch = async (placeId, clinics, maxDistance = 50) => {
  try {
    // Get coordinates from place ID
    const userLocation = await getSearchCoordinatesFromPlaceId(placeId)

    if (!userLocation.success) {
      return {
        success: false,
        error: userLocation.error,
        clinics: clinics,
        userLocation: null
      }
    }

    // Add distance information to clinics
    const clinicsWithDistance = addDistanceToClinic(clinics, userLocation)

    // Filter by distance
    const filteredClinics = filterClinicsByDistance(clinicsWithDistance, maxDistance)

    // Sort by distance
    const sortedClinics = sortClinicsByDistance(filteredClinics)

    return {
      success: true,
      clinics: sortedClinics,
      userLocation,
      searchRadius: maxDistance,
      totalClinicsFound: sortedClinics.length,
      clinicsWithinRadius: sortedClinics.filter((c) => c.distance !== null).length
    }
  } catch (error) {
    console.error('Error processing location-based search:', error)
    return {
      success: false,
      error: error.message,
      clinics: clinics,
      userLocation: null
    }
  }
}

/**
 * Validate location data
 * @param {Object} location - Location object to validate
 * @returns {boolean} True if location is valid
 */
export const validateLocation = (location) => {
  return (
    location &&
    typeof location.lat === 'number' &&
    typeof location.lng === 'number' &&
    location.lat >= -90 &&
    location.lat <= 90 &&
    location.lng >= -180 &&
    location.lng <= 180
  )
}
